/**
 * WEBSOCKET / SIGNALR FAN SERVICE: FIGHTERS
 * Wrapper de comunicación en tiempo real para el Aficionado Móvil de Fighters.
 * Gestiona la conexión persistente con SignalR Hub para recibir la cuenta regresiva sincronizada,
 * telemetría de vida de los luchadores, eventos de combos, barajas y pantalla de fin de partida.
 * 
 * @module utilities/WS/FightersFanWS
 */

import SignalRService from './SignalRService';

/**
 * Referencia al handler de escucha registrado en SignalRService para mensajes genéricos.
 * @type {Function|null}
 */
let registeredMessageHandler = null;

/**
 * Referencia al handler de reconexión registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectedHandler = null;

/**
 * Referencia al handler de reconexión en progreso.
 * @type {Function|null}
 */
let registeredReconnectingHandler = null;

/**
 * Referencia al handler de cierre definitivo de conexión.
 * @type {Function|null}
 */
let registeredCloseHandler = null;

/**
 * Conecta el dispositivo móvil del aficionado al Hub de SignalR con el rol 'fighters:fan'.
 * Soporta polimórficamente tanto el orden de parámetros de Interaktia-V2 como el de ADSL.
 * @param {Function|string} [param1] - Callback onIncomingMessage o playerChoice ('J1' | 'J2').
 * @param {Function} [param2] - Callback onStatusChange o onIncomingMessage.
 * @param {string|Function} [param3] - Identificador GUID roomId o callback onStatusChange.
 * @param {string} [param4] - Identificador GUID roomId o playerChoice.
 */
export const ConnectWebSocketForFightersFan = (
    param1,
    param2,
    param3,
    param4
) => {
    let onIncomingMessage = () => {};
    let onStatusChange = () => {};
    let roomId = '';
    let playerChoice = '';

    if (typeof param1 === 'function') {
        // Interaktia V2 signature: (onIncomingMessage, onStatusChange, roomId, playerChoice)
        onIncomingMessage = param1;
        onStatusChange = typeof param2 === 'function' ? param2 : () => {};
        roomId = typeof param3 === 'string' ? param3 : '';
        playerChoice = typeof param4 === 'string' ? param4 : '';
    } else {
        // ADSL signature: (playerChoice, onIncomingMessage, onStatusChange, roomId)
        playerChoice = typeof param1 === 'string' ? param1 : '';
        onIncomingMessage = typeof param2 === 'function' ? param2 : () => {};
        onStatusChange = typeof param3 === 'function' ? param3 : () => {};
        roomId = typeof param4 === 'string' ? param4 : '';
    }

    onStatusChange('connecting');

    // Desuscribir handlers previos si existieran para evitar fugas de memoria o ejecuciones duplicadas
    if (registeredMessageHandler) {
        SignalRService.off('message', registeredMessageHandler);
    }
    if (registeredReconnectedHandler) {
        SignalRService.off('reconnected', registeredReconnectedHandler);
    }
    if (registeredReconnectingHandler) {
        SignalRService.off('reconnecting', registeredReconnectingHandler);
    }
    if (registeredCloseHandler) {
        SignalRService.off('close', registeredCloseHandler);
    }

    // Handler de mensajes entrantes con filtrado estricto por sala si fue provista
    registeredMessageHandler = (messageEnvelope) => {
        if (!messageEnvelope) return;

        const envelopeRoomId = messageEnvelope.roomId
            || messageEnvelope.RoomId
            || messageEnvelope.payload?.roomId
            || messageEnvelope.payload?.RoomId
            || messageEnvelope.data?.roomId
            || messageEnvelope.data?.RoomId;

        if (roomId && envelopeRoomId && String(envelopeRoomId).toLowerCase() !== String(roomId).toLowerCase()) {
            return;
        }

        if (typeof onIncomingMessage === 'function') {
            onIncomingMessage(messageEnvelope);
        }
    };

    registeredReconnectedHandler = () => {
        onStatusChange('connected');
    };

    registeredReconnectingHandler = () => {
        onStatusChange('reconnecting');
    };

    registeredCloseHandler = () => {
        onStatusChange('closed');
    };

    SignalRService.on('message', registeredMessageHandler);
    SignalRService.on('reconnected', registeredReconnectedHandler);
    SignalRService.on('reconnecting', registeredReconnectingHandler);
    SignalRService.on('close', registeredCloseHandler);

    SignalRService.connect({
        tipo: 'fighters:fan',
        roomId: roomId || '',
        playerChoice: playerChoice || ''
    })
        .then(() => {
            onStatusChange('connected');
        })
        .catch((connectionError) => {
            console.error('[FightersFanWS] Error al establecer conexión del aficionado con el Hub de SignalR:', connectionError);
            onStatusChange('error');
        });
};

/**
 * Desconecta limpiamente la sesión SignalR del aficionado móvil y remueve los escuchas registrados.
 */
export const DisconnectWebSocketForFightersFan = () => {
    try {
        if (registeredMessageHandler) {
            SignalRService.off('message', registeredMessageHandler);
            registeredMessageHandler = null;
        }
        if (registeredReconnectedHandler) {
            SignalRService.off('reconnected', registeredReconnectedHandler);
            registeredReconnectedHandler = null;
        }
        if (registeredReconnectingHandler) {
            SignalRService.off('reconnecting', registeredReconnectingHandler);
            registeredReconnectingHandler = null;
        }
        if (registeredCloseHandler) {
            SignalRService.off('close', registeredCloseHandler);
            registeredCloseHandler = null;
        }
        SignalRService.disconnect();
    } catch (disconnectionError) {
        console.error('[FightersFanWS] Error al desconectar la sesión de SignalR del aficionado:', disconnectionError);
    }
};

// Aliases de compatibilidad con ADSL
export const ConnectWebSocketForFarmeandoAuraFan = ConnectWebSocketForFightersFan;
export const DisconnectWebSocketForFarmeandoAuraFan = DisconnectWebSocketForFightersFan;

export default {
    ConnectWebSocketForFightersFan,
    DisconnectWebSocketForFightersFan,
    ConnectWebSocketForFarmeandoAuraFan,
    DisconnectWebSocketForFarmeandoAuraFan
};
